import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Layout from "./components/layout/Layout";
import HomePage from "./pages/HomePage";
import TournamentsPage from "./pages/tournaments/TournamentsPage";
import NewTournamentPage from "./pages/tournaments/NewTournamentPage";
import TeamsPage from "./pages/teams/TeamsPage";
import TeamDetailPage from "./pages/teams/TeamDetailPage";
import NewTeamPage from "./pages/teams/NewTeamPage";
import PlayersPage from "./pages/players/PlayersPage";
import PlayerDetailPage from "./pages/players/PlayerDetailPage";
import NewPlayerPage from "./pages/players/NewPlayerPage";
import MatchesPage from "./pages/matches/MatchesPage";
import NewMatchPage from "./pages/matches/NewMatchPage";
import MatchScoringPage from "./pages/matches/MatchScoringPage";
import StatisticsPage from "./pages/statistics/StatisticsPage";
import FeedbackPage from "./pages/feedback/FeedbackPage";
import NotFound from "./pages/NotFound";
import AuthPage from "./pages/auth/AuthPage";
import UserAccountPage from "./pages/user/UserAccountPage";
import SystemSettingsPage from "./pages/settings/SystemSettingsPage";
// Footer pages
import FeaturesPage from "./pages/footer/FeaturesPage";
import AboutUsPage from "./pages/footer/AboutUsPage";
import ContactPage from "./pages/footer/ContactPage";
import PrivacyPolicyPage from "./pages/footer/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/footer/TermsOfServicePage";
import DisclaimerPage from "./pages/footer/DisclaimerPage";
import FAQPage from "./pages/footer/FAQPage";
import PricingPage from "./pages/footer/PricingPage";
import { AuthProvider } from "./contexts/AuthContext";
import AuthGuard from "./components/layout/AuthGuard";
import { ThemeProvider } from "next-themes";
import { SettingsProvider } from "./contexts/SettingsContext";
import './styles/slider.css';
import { useIsMobile } from './mobile/hooks/useMobileDetection';
import MobileLayout from './mobile/components/layout/MobileLayout';
import MobileHomePage from './mobile/pages/MobileHomePage';
import MobileMatchScoringPage from './mobile/pages/MobileMatchScoringPage';
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Analytics } from "@vercel/analytics/react";
import { Button } from "@/components/ui/button";
import { DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MoreHorizontal } from "lucide-react";
import AddUmpirePage from "./pages/umpires/AddUmpirePage";
import UmpiresPage from "./pages/umpires/UmpiresPage";
import EditUmpirePage from "./pages/umpires/EditUmpirePage";
import UmpireProfilePage from "./pages/umpires/UmpireProfilePage";
import LiveScoreDashboard from './pages/matches/LiveScoreDashboard';
import AdminControllerPage from './pages/admin/AdminControllerPage';

const queryClient = new QueryClient();

const App = () => {
  const isMobile = useIsMobile();

  const wrapWithLayout = (content: React.ReactNode) => {
    return isMobile ? <MobileLayout>{content}</MobileLayout> : <Layout>{content}</Layout>;
  };

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AuthProvider>
              <SettingsProvider>
                <div className="mobile-container">
                  <Routes>
                    <Route path="/auth" element={<AuthPage />} />
                    <Route path="/" element={
                      isMobile
                        ? <MobileLayout><MobileHomePage /></MobileLayout>
                        : <Layout><HomePage /></Layout>
                    } />
                    <Route path="/tournaments" element={wrapWithLayout(<AuthGuard><TournamentsPage /></AuthGuard>)} />
                    <Route path="/tournaments/new" element={wrapWithLayout(<AuthGuard><NewTournamentPage /></AuthGuard>)} />
                    <Route path="/teams" element={wrapWithLayout(<AuthGuard><TeamsPage /></AuthGuard>)} />
                    <Route path="/teams/new" element={wrapWithLayout(<AuthGuard><NewTeamPage /></AuthGuard>)} />
                    <Route path="/teams/:id" element={wrapWithLayout(<AuthGuard><TeamDetailPage /></AuthGuard>)} />
                    <Route path="/players" element={wrapWithLayout(<AuthGuard><PlayersPage /></AuthGuard>)} />
                    <Route path="/players/new" element={wrapWithLayout(<AuthGuard><NewPlayerPage /></AuthGuard>)} />
                    <Route path="/players/:id" element={wrapWithLayout(<PlayerDetailPage />)} />
                    <Route path="/umpires/new" element={wrapWithLayout(<AuthGuard><AddUmpirePage /></AuthGuard>)} />
                    <Route path="/umpires" element={wrapWithLayout(<AuthGuard><UmpiresPage /></AuthGuard>)} />
                    <Route path="/umpires/edit/:id" element={wrapWithLayout(<AuthGuard><EditUmpirePage /></AuthGuard>)} />
                    <Route path="/umpires/:id" element={wrapWithLayout(<UmpireProfilePage />)} />
                    <Route path="/matches" element={wrapWithLayout(<AuthGuard><MatchesPage /></AuthGuard>)} />
                    <Route path="/matches/new" element={wrapWithLayout(<AuthGuard><NewMatchPage /></AuthGuard>)} />
                    <Route path="/matches/:id" element={
                      isMobile
                        ? <MobileLayout><MobileMatchScoringPage /></MobileLayout>
                        : <Layout><AuthGuard><MatchScoringPage /></AuthGuard></Layout>
                    } />
                    <Route path="/matches/live/:id" element={
                      isMobile
                        ? <MobileLayout><LiveScoreDashboard /></MobileLayout>
                        : <Layout><LiveScoreDashboard /></Layout>
                    } />
                    <Route path="/statistics" element={wrapWithLayout(<AuthGuard><StatisticsPage /></AuthGuard>)} />
                    <Route path="/feedback" element={wrapWithLayout(<AuthGuard><FeedbackPage /></AuthGuard>)} />
                    <Route path="/user-account" element={wrapWithLayout(<AuthGuard><UserAccountPage /></AuthGuard>)} />
                    <Route path="/settings" element={wrapWithLayout(<AuthGuard><SystemSettingsPage /></AuthGuard>)} />
                    <Route path="/features" element={wrapWithLayout(<FeaturesPage />)} />
                    <Route path="/about" element={wrapWithLayout(<AboutUsPage />)} />
                    <Route path="/contact" element={wrapWithLayout(<ContactPage />)} />
                    <Route path="/privacy" element={wrapWithLayout(<PrivacyPolicyPage />)} />
                    <Route path="/terms" element={wrapWithLayout(<TermsOfServicePage />)} />
                    <Route path="/disclaimer" element={wrapWithLayout(<DisclaimerPage />)} />
                    <Route path="/faq" element={wrapWithLayout(<FAQPage />)} />
                    <Route path="/pricing" element={wrapWithLayout(<PricingPage />)} />
                    <Route path="/admin/controller" element={wrapWithLayout(<AuthGuard><AdminControllerPage /></AuthGuard>)} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </div>
              </SettingsProvider>
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </ThemeProvider>

      {/* ✅ Add Vercel analytics tools */}
      <SpeedInsights />
      <Analytics />
    </QueryClientProvider>
  );
};

export default App;
